import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api.js";

// Cached catalogue (hits /api/vehicle-catalogue once per session).
let _cache = null;
let _pending = null;

export async function loadVehicleCatalogue() {
  if (_cache) return _cache;
  if (!_pending) _pending = api.get("/vehicle-catalogue").then((r) => (_cache = r.data));
  return _pending;
}

export function invalidateVehicleCatalogue() {
  _cache = null;
  _pending = null;
}

/**
 * useVehicleCatalogue — provides lists of options for Brand, Model, Generation
 * dropdowns given current (brand, model) selection.
 *
 * Returns:
 *   catalogue, brands, models, generations, loading
 */
export function useVehicleCatalogue(brand, model) {
  const [catalogue, setCatalogue] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    if (_cache) return;
    loadVehicleCatalogue()
      .then((data) => alive && setCatalogue(data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const brands = useMemo(() => catalogue.map((b) => b.brand), [catalogue]);
  const freeTextBrand = useMemo(() => catalogue.find((b) => b.is_free_text)?.brand, [catalogue]);
  const brandEntry = useMemo(() => catalogue.find((b) => b.brand === brand), [catalogue, brand]);
  const models = useMemo(() => brandEntry?.models.map((m) => m.name) || [], [brandEntry]);
  const modelEntry = useMemo(() => brandEntry?.models.find((m) => m.name === model), [brandEntry, model]);
  const generations = useMemo(() =>
    modelEntry?.generations.map((g) => ({
      name: g.name,
      label: g.display_label || `${g.name}${g.year_from || g.year_to ? ` (${g.year_from || "?"}–${g.year_to || "actual"})` : ""}`,
    })) || [],
    [modelEntry]
  );

  return { catalogue, brands, freeTextBrand, models, generations, loading };
}


/**
 * <VehicleTriplePicker> — compact cascading Brand → Model → Generation dropdown.
 *
 * Special "Outros" sentinel: whenever the user picks "Outros" at any level,
 * the next levels switch to free-text inputs so unlisted brands/models/
 * generations can still be captured without polluting the catalogue.
 *
 * Props:
 *   brand, model, generation (controlled values)
 *   onChange({brand, model, generation})
 *   required: boolean (defaults to false — generation is always optional)
 *   compact: boolean — renders inputs in a single row instead of stacked
 *   testidPrefix: prefix for data-testid (e.g. "som" → "som-brand", "som-model", "som-generation")
 */
export function VehicleTriplePicker({
  brand, model, generation,
  onChange,
  required = false,
  compact = true,
  testidPrefix = "vehicle",
  disabled = false,
}) {
  const { brands, freeTextBrand, models, generations, loading } = useVehicleCatalogue(brand, model);

  // Internal "free-text mode" flags — set either when the user explicitly picks
  // "Outros", or when a stored value isn't in the current catalogue (e.g. the
  // record was created before the catalogue existed).
  const [brandFree, setBrandFree] = React.useState(false);
  const [modelFree, setModelFree] = React.useState(false);
  const [genFree, setGenFree] = React.useState(false);

  // Reconcile against incoming values: if brand/model/generation isn't in the
  // live lists, flip into free-text mode automatically.
  React.useEffect(() => {
    if (loading) return;
    if (brand && !brands.includes(brand) && brand !== "Outros") setBrandFree(true);
    else if (!brandFree) setBrandFree(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, brands, loading]);
  React.useEffect(() => {
    if (loading) return;
    if (model && !models.includes(model) && model !== "Outros") setModelFree(true);
    else if (brandFree) setModelFree(true);
    else if (!modelFree) setModelFree(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, models, brandFree, loading]);
  React.useEffect(() => {
    if (loading) return;
    if (generation && !generations.some((g) => g.name === generation) && generation !== "Outros") setGenFree(true);
    else if (modelFree) setGenFree(true);
    else if (!genFree) setGenFree(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation, generations, modelFree, loading]);

  const sel = (nb, nm, ng) => onChange({ brand: nb, model: nm, generation: ng });

  const commonCls = "text-sm border border-gray-200 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30 bg-white disabled:opacity-60";
  const wrap = compact ? "grid grid-cols-3 gap-2" : "space-y-2";

  // Reorder brands so the free-text sentinel appears last if present
  const catalogueBrands = brands.filter((b) => b !== freeTextBrand);
  const hasOthersBrand = Boolean(freeTextBrand);

  const handleBrandChange = (v) => {
    if (v === "__other__") {
      setBrandFree(true);
      setModelFree(true);
      setGenFree(true);
      sel("", "", "");
    } else {
      setBrandFree(false);
      setModelFree(false);
      setGenFree(false);
      sel(v || null, null, null);
    }
  };

  const handleModelChange = (v) => {
    if (v === "__other__") {
      setModelFree(true);
      setGenFree(true);
      sel(brand, "", "");
    } else {
      setModelFree(false);
      setGenFree(false);
      sel(brand, v || null, null);
    }
  };

  const handleGenChange = (v) => {
    if (v === "__other__") {
      setGenFree(true);
      sel(brand, model, "");
    } else {
      setGenFree(false);
      sel(brand, model, v || null);
    }
  };

  return (
    <div className={wrap} data-testid={`${testidPrefix}-triple-picker`}>
      <div>
        {!compact && <label className="text-xs text-[#6C757D] block mb-1">Marca {required && "*"}</label>}
        {brandFree ? (
          <input
            autoFocus
            value={brand || ""}
            onChange={(e) => sel(e.target.value, model, generation)}
            placeholder="Nome da marca…"
            data-testid={`${testidPrefix}-brand-free`}
            className={commonCls + " w-full"}
          />
        ) : (
          <select
            value={brand || ""}
            onChange={(e) => handleBrandChange(e.target.value)}
            data-testid={`${testidPrefix}-brand`}
            disabled={disabled || loading}
            className={commonCls + " w-full"}
          >
            <option value="">— Marca —</option>
            {catalogueBrands.map((b) => (<option key={b} value={b}>{b}</option>))}
            {hasOthersBrand && <option value="__other__">Outros… (texto livre)</option>}
          </select>
        )}
      </div>
      <div>
        {!compact && <label className="text-xs text-[#6C757D] block mb-1">Modelo {required && "*"}</label>}
        {modelFree ? (
          <input
            value={model || ""}
            onChange={(e) => sel(brand, e.target.value, generation)}
            placeholder="Nome do modelo…"
            data-testid={`${testidPrefix}-model-free`}
            className={commonCls + " w-full"}
            disabled={disabled || !brand}
          />
        ) : (
          <select
            value={model || ""}
            onChange={(e) => handleModelChange(e.target.value)}
            data-testid={`${testidPrefix}-model`}
            disabled={disabled || loading || !brand}
            className={commonCls + " w-full"}
          >
            <option value="">— Modelo —</option>
            {models.map((m) => (<option key={m} value={m}>{m}</option>))}
            {models.length > 0 && <option value="__other__">Outros… (texto livre)</option>}
          </select>
        )}
      </div>
      <div>
        {!compact && <label className="text-xs text-[#6C757D] block mb-1">Geração</label>}
        {genFree ? (
          <input
            value={generation || ""}
            onChange={(e) => sel(brand, model, e.target.value)}
            placeholder="Geração…"
            data-testid={`${testidPrefix}-generation-free`}
            className={commonCls + " w-full"}
            disabled={disabled || !model}
          />
        ) : (
          <select
            value={generation || ""}
            onChange={(e) => handleGenChange(e.target.value)}
            data-testid={`${testidPrefix}-generation`}
            disabled={disabled || loading || !model || generations.length === 0}
            className={commonCls + " w-full"}
          >
            <option value="">— Geração —</option>
            {generations.map((g) => (<option key={g.name} value={g.name}>{g.label}</option>))}
            {generations.length > 0 && <option value="__other__">Outros… (texto livre)</option>}
          </select>
        )}
      </div>
    </div>
  );
}
